import {
  Component,
  ViewChild,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { Options, ChangeContext, LabelType } from '@angular-slider/ngx-slider';
import { takeUntil } from 'rxjs/operators';
import constants from 'src/constants.json';

import { State, StateList } from '../../shared/state-list';
import { Listing } from '../../shared/property-details';
import { ListingDtls } from '../../shared/listing-details';
import { PropertyDetailsService } from '../../services/property-details.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { ListingLeadsService } from '../../services/listing-leads.service';
import { ToastService, ToastType } from '../../services/toast.service';
import { AppState } from 'src/app/app.state';
import { Subject } from 'rxjs';
import { ActivityLoggerService } from 'src/app/services/activity-logger.service';
import { UserState } from 'src/state/user.state';
import { CookieService } from 'ngx-cookie-service';

@Component({
  selector: 'app-landing-screen',
  templateUrl: './landing-screen.component.html',
  styleUrls: ['./landing-screen.component.css'],
})
export class LandingScreenComponent {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private ref: ChangeDetectorRef,
    public propertyDetailsService: PropertyDetailsService,
    public localStorageService: LocalStorageService,
    public listingLeadsService: ListingLeadsService,
    public toastService: ToastService,
    public appState: AppState,
    private userState: UserState,
    private activityLoggerService: ActivityLoggerService,
    public cookieService: CookieService,
  ) {}

  @ViewChild(GoogleMap, { static: false }) map: GoogleMap;
  @ViewChild(MapInfoWindow, { static: false }) infoWindow: MapInfoWindow;

  stateList: State[] = StateList;
  mapZoom = 14;
  mapCenter: any;
  mapOptions: any;

  markerInfoContent = '';
  markerOptions: any;
  locationCoords: any;

  lat: number | null = null;
  lng: number | null = null;
  isEdit: boolean = false;
  address!: string;
  propertyDetails: Listing;
  listingDetails: ListingDtls;
  streetAddress: string | null = null;
  city: string | null = null;
  zipCode: string | null = null;
  state: string | null = null;
  unitNumber: string | null = '';
  propertySubType: string | null = null;
  imgUrl: string | null = null;
  isButtonLoader: boolean = false;
  ListingLeadId: string;
  companyName: any;
  minSavings: number;
  maxSavings: number;

  sellingPrice: number = 0;
  sliderValue: number = 1000000;
  sliderMin: number = 0;
  sliderMax: number = 2000000;

  isLoggedIn: boolean = false;
  onDestroyNotifier$ = new Subject();
  restrictedStates = [];
  myHomeId;

  options: Options = {
    floor: Math.round(this.sliderMin),
    ceil: Math.round(this.sliderMax),
    showSelectionBar: true,
    getSelectionBarColor: (value: number): string => {
      return '#55A29C';
    },
    translate: (value: number, label: LabelType): string => {
      if (value >= 1000000) {
        return (
          '<div class="tooltip">' +
          '<b>$</b>' +
          (value / 1000000).toFixed(2) +
          '<b>M</b>' +
          '</div>'
        );
      } else {
        return (
          '<div class="tooltip">' +
          '<b>$</b>' +
          Math.round(value / 1000) +
          '<b>K</b>' +
          '</div>'
        );
      }
    },
    getLegend: (value: number): string => {
      if (value >= 1000000) {
        return (value / 1000000).toFixed(2) + '<b>M</b>';
      } else {
        return Math.round(value / 1000) + '<b>K</b>';
      }
    },
  };

  ngOnInit() {
    this.isButtonLoader = true;
    this.route.queryParams.subscribe((params) => {
      this.myHomeId = params['homeId'] ? params['homeId'] : null;

      this.address = params['address'] ? params['address'] : null;
      this.streetAddress = params['street']
        ? params['street_number']
          ? params['street_number'] + ' ' + params['street']
          : params['street']
        : null;

      if (typeof params['city'] == 'object') {
        let cityLen = params['city'].length;
        this.city = params['city'][cityLen - 1];
      } else {
        this.city = params['city'] ? params['city'] : null;
      }

      this.state = params['state'] ? params['state'] : null;
      this.zipCode = params['zip'] ? params['zip'] : null;

      if (this.address) {
        let addressInfo = {
          matchAddress: this.address.replace(/[\s,]+/g, '').toLocaleLowerCase(),
          address: this.address,
          streetAddress: this.streetAddress,
          city: this.city,
          state: this.state,
          zipCode: this.zipCode,
        };
        this.localStorageService.set(
          'addressInfo',
          JSON.stringify(addressInfo),
        );
        this.getPropertyData();
        this.setListingLeads();
      }
    });

    let addressInfo = this.localStorageService.get('addressInfo');
    if (addressInfo && !this.address) {
      let addressData = JSON.parse(addressInfo);
      this.address = addressData.address;
      this.streetAddress = addressData.streetAddress;
      this.city = addressData.city;
      this.state = addressData.state;
      this.zipCode = addressData.zipCode;
      this.unitNumber = addressData.unitNumber ? addressData.unitNumber : '';
      this.getPropertyData();
      this.setListingLeads();
    }
    this.refresh();

    this.appState.companyInfo$
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res) => {
        if (res) {
          this.companyName = res.name;
          if (res?.sellSideConfig && res?.sellSideConfig?.restrictedStates) {
            this.restrictedStates = res?.sellSideConfig?.restrictedStates;
          }
        }
      });
  }

  getPropertyData() {
    this.propertyDetailsService
      .GetPropertyData(
        this.streetAddress,
        this.city,
        this.state,
        this.zipCode,
        this.unitNumber,
      )
      .subscribe({
        next: (res) => {
          this.isButtonLoader = false;

          if (
            res.Status &&
            res.Status.Message == 'Success' &&
            res.Status.Code == 200
          ) {
            if (
              res.data &&
              res.data.listingDetails &&
              res.data.listingDetails.length > 0
            ) {
              this.listingDetails = res.data.listingDetails[0];
            } else {
              this.listingDetails = null;
            }

            if (
              res.data &&
              res.data.propertyDetails &&
              res.data.propertyDetails.Listing
            ) {
              this.propertyDetails = res.data.propertyDetails.Listing;
            } else {
              this.propertyDetails = null;
            }

            this.lat =
              this.listingDetails && this.listingDetails.Latitude
                ? parseFloat(this.listingDetails.Latitude)
                : this.propertyDetails && this.propertyDetails.Latitude
                  ? parseFloat(this.propertyDetails.Latitude)
                  : null;

            this.lng =
              this.listingDetails && this.listingDetails.Longitude
                ? parseFloat(this.listingDetails.Longitude)
                : this.propertyDetails && this.propertyDetails.Longitude
                  ? parseFloat(this.propertyDetails.Longitude)
                  : null;

            this.propertySubType =
              this.listingDetails && this.listingDetails.PropertySubType
                ? this.listingDetails.PropertySubType
                : this.propertyDetails && this.propertyDetails.PropertySubType
                  ? this.propertyDetails.PropertySubType
                  : '';

            if (this.lat && this.lng) {
              this.getMapLocation();
            }

            if (
              res.data &&
              res.data.imageURLs &&
              res.data.imageURLs.length > 0 &&
              res.data.imageURLs[0] != 'N'
            ) {
              this.imgUrl = res.data.imageURLs[0];
            }

            let mrkIns = null;
            if (
              res.data &&
              res.data.marketInsight &&
              res.data.marketInsight.analysis &&
              res.data.marketInsight.analysis.length > 0
            ) {
              mrkIns = res.data.marketInsight.analysis[0].MedianListPrice;

              if (
                this.propertySubType &&
                this.propertySubType.toLowerCase() == 'single family residence'
              ) {
                this.sellingPrice = mrkIns['SingleFamily'];
              } else if (
                this.propertySubType &&
                this.propertySubType.toLowerCase() == 'condominium'
              ) {
                this.sellingPrice = mrkIns['Condominium'];
              } else if (
                this.propertySubType &&
                this.propertySubType.toLowerCase() == 'townhouse'
              ) {
                this.sellingPrice = mrkIns['TownHouse'];
              } else if (
                this.propertySubType &&
                (this.propertySubType.toLowerCase() == 'multifamily' ||
                  this.propertySubType.toLowerCase() == 'multi family > 4')
              ) {
                this.sellingPrice = mrkIns['Multifamily'];
              } else if (
                this.propertySubType &&
                this.propertySubType.toLowerCase() == 'apartment'
              ) {
                this.sellingPrice = mrkIns['Apartment'];
              } else if (
                this.propertySubType &&
                this.propertySubType.toLowerCase() == 'residential'
              ) {
                this.sellingPrice = mrkIns['Residential'];
              } else if (
                this.propertySubType &&
                this.propertySubType.toLowerCase() == 'nonresidential'
              ) {
                this.sellingPrice = mrkIns['NonResidential'];
              } else {
                this.sellingPrice = mrkIns['All'];
              }

              this.sliderValue = this.sellingPrice;
              this.sliderMin = 0;
              if (this.sellingPrice < 1000000) {
                this.sliderMax = 2000000;
              } else if (this.sellingPrice < 2000000) {
                this.sliderMax = 3000000;
              } else if (this.sellingPrice < 3000000) {
                this.sliderMax = 5000000;
              } else if (this.sellingPrice < 4000000) {
                this.sliderMin = 1000000;
                this.sliderMax = 7000000;
              } else {
                this.sliderMin = 1000000;
                this.sliderMax = 8000000;
              }

              const newOptions: Options = Object.assign({}, this.options);
              newOptions.floor = Math.round(this.sliderMin);
              newOptions.ceil = Math.round(this.sliderMax);

              this.options = newOptions;

              this.minSavings = Math.round(this.sellingPrice * 0.03 - 590);
              this.minSavings = this.minSavings < 0 ? 0 : this.minSavings;
              this.maxSavings = Math.round(this.sellingPrice * 0.05 - 590);
              this.maxSavings = this.maxSavings < 0 ? 0 : this.maxSavings;
            }
          } else {
            console.log('GetPropertyData Not Found !!!');
          }
        },
        error: (err) => {
          this.isButtonLoader = false;
          console.log('GetPropertyData err : ', err);
        },
      });
  }

  getMapLocation() {
    const point: google.maps.LatLngLiteral = {
      lat: this.lat,
      lng: this.lng,
    };

    this.locationCoords = new google.maps.LatLng(point);

    this.mapZoom = 16;

    this.mapCenter = new google.maps.LatLng(point);
    this.mapOptions = {
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      zoomControl: true,
      scrollwheel: false,
      disableDoubleClickZoom: true,
      maxZoom: 50,
      minZoom: 4,
    };
    this.map.panTo(point);

    this.markerOptions = {
      draggable: true,
      animation: google.maps.Animation.DROP,
      icon: '../../../assets/images/map_icon.png',
    };
    this.ref.detectChanges();
  }

  setListingLeads() {
    let userId = this.userState?.getUserDetails?.id;
    let input = {
      fullAddress: this.address,
      StreetAddress: this.streetAddress,
      City: this.city,
      StateOrProvince: this.state,
      PostalCode: this.zipCode,
      UnitNumber: this.unitNumber,
      userId: userId,
      createdDate: new Date(),
    };

    input = this.filterInputJson(input);

    this.listingLeadsService.CreateListingLeads(input).subscribe({
      next: (res) => {
        if (res) {
          this.ListingLeadId = res.id;
        } else {
          console.log('CreateListingLeads Fails!!!!');
        }
      },
      error: (err) => {
        console.log('CreateListingLeads err!!!!');
        console.log(err);
      },
    });
  }

  openInfoWindow(marker: MapMarker) {
    this.infoWindow.open(marker);
  }

  onUserChange(changeContext: ChangeContext): void {
    this.sliderValue = changeContext.value;
    this.sellingPrice = changeContext.value;

    this.minSavings = Math.round(this.sellingPrice * 0.03 - 590);
    this.minSavings = this.minSavings < 0 ? 0 : this.minSavings;
    this.maxSavings = Math.round(this.sellingPrice * 0.05 - 590);
    this.maxSavings = this.maxSavings < 0 ? 0 : this.maxSavings;
  }

  public toggleButton(): void {
    if (
      this.streetAddress &&
      this.city &&
      this.state &&
      this.zipCode &&
      this.isEdit == true
    ) {
      this.address =
        this.streetAddress +
        ', ' +
        this.city +
        ', ' +
        this.state +
        ' ' +
        this.zipCode;
      let addressInfo = {
        matchAddress: this.address.replace(/[\s,]+/g, '').toLocaleLowerCase(),
        address: this.address,
        streetAddress: this.streetAddress,
        city: this.city,
        state: this.state,
        zipCode: this.zipCode,
      };
      if (this.unitNumber) {
        this.address = this.unitNumber + ' ' + this.address;
        addressInfo['unitNumber'] = this.unitNumber;
        addressInfo['address'] = this.address;
        addressInfo['matchAddress'] = this.address
          .replace(/[\s,]+/g, '')
          .toLocaleLowerCase();
      }
      this.localStorageService.set('addressInfo', JSON.stringify(addressInfo));
      this.getPropertyData();
      this.isEdit = !this.isEdit;
    } else {
      this.isEdit = true;
    }
  }

  startListing() {
    if (
      this.restrictedStates &&
      this.restrictedStates.length > 0 &&
      this.restrictedStates.indexOf(this.state.toUpperCase()) > -1
    ) {
      this.toastService.toast(
        'Sorry! currently, we are not serving this location.',
        ToastType.Warning,
      );
      return;
    }
    let userType = this.userState?.getUserDetails?.userType;
    let userName = this.userState?.getUserDetails?.userName;
    let accessToken = this.cookieService.get('accessToken');
    let userId = this.userState?.getUserDetails?.id;
    if (userType == 'investor') {
      let loggerParams = {
        category: constants.LOGGER.listMyProperty.category,
        action: constants.LOGGER.listMyProperty.action.startListing.key,
        data: {
          userId: userId,
          propertyId: this.propertyDetails?.PMXPropertyId,
        },
      };
      this.activityLoggerService.updateActivityLog(loggerParams).subscribe({
        next: (res) => {},
        error: (err) => {
          console.log(err);
        },
      });
    }

    this.localStorageService.set('isBasicHomeExpanded', false);

    let historyState = {
      matchAddress: this.address.replace(/[\s,]+/g, '').toLocaleLowerCase(),
      address: this.address,
      City: this.city,
      StateOrProvince: this.state,
      PostalCode: this.zipCode,
      UnitNumber: this.unitNumber,
      StreetAddress: this.streetAddress,
      minSavings: this.minSavings,
      maxSavings: this.maxSavings,
      ListPrice: null,
      OnMarketDate: null,
      listingDetails: this.listingDetails,
      propertyDetails: this.propertyDetails,
      PropertySubType: this.propertySubType,
      imgUrl: this.imgUrl,
      FIPS:
        this.listingDetails && this.listingDetails.FIPS
          ? this.listingDetails.FIPS
          : this.propertyDetails && this.propertyDetails.FIPS
            ? this.propertyDetails.FIPS
            : null,
      ListingLeadId: this.ListingLeadId,
      myHomeId: this.myHomeId,
    };

    if (userName && accessToken && userId) {
      this.router.navigateByUrl('/home-details', {
        state: historyState,
      });
    } else {
      this.router.navigateByUrl('/signup', {
        state: historyState,
      });
    }
  }

  filterInputJson(obj) {
    Object.keys(obj).forEach((key) => {
      if (obj[key] == null) {
        delete obj[key];
      }
    });
    return obj;
  }

  // CHECKING WHETHER USER LOGGED IN OR NOT
  refresh() {
    let accessToken = this.cookieService.get('accessToken');
    let userId = this.userState?.getUserDetails?.id;
    let userFullName = this.userState?.getUserDetails?.userFullName;
    if (userId && accessToken && userFullName) {
      this.isLoggedIn = true;
    } else {
      this.isLoggedIn = false;
    }
  }
}
